import toInteger from 'lodash/toInteger';

import { LabelPrintError } from 'label/HubPrint';
import { getZpl } from 'label/LabelPrintTemplate';
import {
  BarcodeDisplayOptions,
  BarcodeTypes,
  LabelContent,
  LabelData,
  LabelEditorItem,
  LabelMargins,
  LabelSize,
  LabelTemplate,
} from 'label/utils/types';
import { socketClient } from 'pages/views/devices/socketClient';
import {
  PRINTER_FORMATS,
  PRINTER_KINDS,
  PRINTER_MESSAGES,
} from 'pages/views/devices/socketClient/types';

import { getHostname } from '../utils';
import { transformToLabelEditorItems } from '../utils/transformItems';

const MAX_PAYLOAD_SIZE = 63_000;

export async function sendLabelToHub({
  labelSize,
  labelData,
  labelMargins,
  labelQuantity,
  isListOfLabels,
  displayPrice,
  barcodeDisplayOption,
  barcodeType,
  labelContent,
  dateNow,
  labelTemplate,
  currencySymbol,
}: {
  labelSize: LabelSize;
  previousLabelSize?: string | undefined;
  labelData: LabelData[];
  labelMargins: LabelMargins;
  labelQuantity: number;
  isListOfLabels: boolean;
  displayPrice?: boolean;
  barcodeDisplayOption?: BarcodeDisplayOptions;
  barcodeType?: BarcodeTypes;
  labelContent?: LabelContent;
  dateNow?: string;
  labelTemplate: LabelTemplate | undefined;
  currencySymbol: string;
}) {
  const executionTimes: Record<string, number> = {
    RETAIL_PRINT_START: Date.now(),
  };

  let chunks: string[] = [];

  // When using the Label Editor, a label template is assigned
  if (labelTemplate) {
    const freeFormFieldValue = JSON.parse(labelTemplate.content)?.fields?.freeForm?.value || '';

    const items: LabelEditorItem[] = await transformToLabelEditorItems(
      labelData,
      !isListOfLabels ? labelQuantity : undefined,
      freeFormFieldValue
    );

    const response = await fetch(`https://label-editor.${getHostname()}/api/transform`, {
      method: 'POST',
      body: JSON.stringify({
        template: labelTemplate,
        items,
        meta: {
          labelShift: (labelMargins[labelSize].left || 0) * -1,
          labelTop: labelMargins[labelSize].top || 0,
          currencySymbol,
        },
      }),
    });

    const zpl = await response.text();

    if (response.status !== 200) {
      throw new Error(LabelPrintError.ParsingError);
    }

    // TODO: Handle properly the chunking process in LSR-16225
    chunks = [zpl];
  } else {
    chunks = labelData.reduce(
      (accumulator, item, index) => {
        const nextZplString = getZpl({
          setHeaderCommands: !index,
          labelData: item,
          labelMargins: {
            [LabelSize.NORMAL]: {
              left:
                labelMargins[LabelSize.NORMAL].left !== 0
                  ? labelMargins[LabelSize.NORMAL].left * -1
                  : labelMargins[LabelSize.NORMAL].left,
              top: labelMargins[LabelSize.NORMAL].top,
            },
            [LabelSize.SMALL]: {
              left:
                labelMargins[LabelSize.SMALL].left !== 0
                  ? labelMargins[LabelSize.SMALL].left * -1
                  : labelMargins[LabelSize.SMALL].left,
              top: labelMargins[LabelSize.SMALL].top,
            },
            [LabelSize.ALT]: {
              left:
                labelMargins[LabelSize.ALT].left !== 0
                  ? labelMargins[LabelSize.ALT].left * -1
                  : labelMargins[LabelSize.ALT].left,
              top: labelMargins[LabelSize.ALT].top,
            },
            [LabelSize.JEWELRY]: {
              left:
                labelMargins[LabelSize.JEWELRY].left !== 0
                  ? labelMargins[LabelSize.JEWELRY].left * -1
                  : labelMargins[LabelSize.JEWELRY].left,
              top: labelMargins[LabelSize.JEWELRY].top,
            },
          },
          labelQuantity: isListOfLabels ? toInteger(item.copies) : labelQuantity,
          labelSize: labelSize!,
          displayPrice,
          barcodeDisplayOption,
          barcodeType,
          labelContent,
          dateNow,
        });

        // Ably will only allow a max payload of 64kb, if the message size approaches the limit, split the payload across multiple messages
        const previousChunk = accumulator[accumulator.length - 1];

        // Check if the previous chunk plus the next chunk is too large, if so start a new chunk on the end
        if (previousChunk.length + nextZplString.length > MAX_PAYLOAD_SIZE) {
          accumulator.push(nextZplString);
        } else {
          accumulator[accumulator.length - 1] = previousChunk.concat(nextZplString);
        }

        return accumulator;
      },
      ['']
    );
  }

  executionTimes.RETAIL_ZPL_GENERATED = Date.now();

  chunks.forEach((zplData) => {
    socketClient.send(PRINTER_MESSAGES.PRINT, {
      data: zplData,
      kind: PRINTER_KINDS.LABEL,
      size: labelSize,
      format: PRINTER_FORMATS.ZPL,
      executionTimes,
    });
  });
}
